import { CalendarAltIcon } from "assets/icons";
import { ChartRangeType } from "components";
import { getStartOfDateByTimeUnit, getEndOfDateByTimeUnit } from "helpers";
import { useBoolean } from "hooks";
import { t } from "i18next";
/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEventHandler, forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import "./StatisticChartDatePicker.scss";
import { Checkbox } from "ui/Checkbox";
import { Input } from "ui/Input";

import { StatisticChartDatePickerProps } from "./StatisticChartDatePicker.types";

import styles from "./StatisticChartDatePicker.module.scss";

const StatisticChartDatePicker = ({
  withComparativePeriod,
  setWithComparativePeriod,
  value,
  onChange,
  valueForCompare,
  onChangeValueForCompare,
  activeRangeType,
  dateFormat,
  placeholder,
  icon,
  isDisabled
}: StatisticChartDatePickerProps) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(value);
  const [compareDateRange, setCompareDateRange] = useState<
    [Date | null, Date | null]
  >(valueForCompare ?? [null, null]);

  const [startDate, endDate] = dateRange;
  const [startDateForCompare, endDateForCompare] = compareDateRange;

  const {
    value: isOpenDatePicker,
    setFalse: onCloseDatePicker,
    toggle: toggleOpeningDatePicker
  } = useBoolean();

  const {
    value: isOpenDatePickerForCompare,
    setFalse: onCloseDatePickerForCompare,
    toggle: toggleOpeningDatePickerForCompare
  } = useBoolean();

  const CustomInput = forwardRef<
    HTMLInputElement,
    {
      value?: string;
      onClick?: () => void;
      onChange?: ChangeEventHandler<HTMLInputElement>;
      placeholder?: string;
      label?: string;
    }
  >(
    (
      {
        value: inputValue,
        onClick,
        onChange: inputOnChange,
        placeholder: inputPlaceholder,
        label
      },
      ref
    ) => (
      <Input
        ref={ref}
        value={inputValue}
        onClick={onClick}
        onChange={inputOnChange}
        placeholder={inputPlaceholder}
        label={label}
        action={
          icon ?? (
            <CalendarAltIcon
              color="#FB3973"
              onClick={toggleOpeningDatePicker}
              className={styles["react-datepicker__icon"]}
            />
          )
        }
        readOnly
        className={styles["react-datepicker__input"]}
      />
    )
  );

  CustomInput.displayName = "CustomInput";

  const handleChangeForCompare = (date: [Date | null, Date | null]) => {
    if (!onChangeValueForCompare) return;
    switch (activeRangeType) {
      case ChartRangeType.Week:
        setCompareDateRange([
          getStartOfDateByTimeUnit(date[0], ChartRangeType.Week),
          getEndOfDateByTimeUnit(date[0], ChartRangeType.Week)
        ]);
        break;
      case ChartRangeType.Month:
        setCompareDateRange([
          getStartOfDateByTimeUnit(date[0], ChartRangeType.Month),
          getEndOfDateByTimeUnit(date[0], ChartRangeType.Month)
        ]);
        break;
      case ChartRangeType.Year:
        setCompareDateRange([
          getStartOfDateByTimeUnit(date[0], ChartRangeType.Year),
          getEndOfDateByTimeUnit(date[0], ChartRangeType.Year)
        ]);
        break;
      case ChartRangeType.Custom:
        setCompareDateRange([date[0], date[1]]);
        break;
      default:
        break;
    }
  };

  const handleChange = (date: [Date | null, Date | null]) => {
    switch (activeRangeType) {
      case ChartRangeType.Week:
        setDateRange([
          getStartOfDateByTimeUnit(date[0], ChartRangeType.Week),
          getEndOfDateByTimeUnit(date[0], ChartRangeType.Week)
        ]);
        break;
      case ChartRangeType.Month:
        setDateRange([
          getStartOfDateByTimeUnit(date[0], ChartRangeType.Month),
          getEndOfDateByTimeUnit(date[0], ChartRangeType.Month)
        ]);
        break;
      case ChartRangeType.Year:
        setDateRange([
          getStartOfDateByTimeUnit(date[0], ChartRangeType.Year),
          getEndOfDateByTimeUnit(date[0], ChartRangeType.Year)
        ]);
        break;
      case ChartRangeType.Custom:
        setDateRange([date[0], date[1]]);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      onChange([dateRange[0], dateRange[1]]);
      onCloseDatePicker();
    }
  }, [dateRange]);

  useEffect(() => {
    if (compareDateRange[0] && compareDateRange[1]) {
      onChangeValueForCompare?.([compareDateRange[0], compareDateRange[1]]);
      onCloseDatePickerForCompare();
    }
  }, [compareDateRange]);

  useEffect(() => {
    handleChange([new Date(), new Date()]);
    handleChangeForCompare([new Date(), new Date()]);
  }, [activeRangeType]);

  return (
    <div className={styles.container}>
      <div>
        <DatePicker
          {...(activeRangeType === ChartRangeType.Custom
            ? {
                selected: startDate,
                onChange: handleChange,
                startDate,
                endDate,
                selectsRange: true
              }
            : activeRangeType === ChartRangeType.Month ||
                activeRangeType === ChartRangeType.Year
              ? {
                  selected: value[0],
                  onChange: (selectedMonthOrYear) =>
                    handleChange([selectedMonthOrYear, null])
                }
              : {
                  selected: value[0],
                  selectsRange: true,
                  startDate,
                  endDate,
                  onChange: handleChange
                })}
          calendarStartDay={1}
          open={isOpenDatePicker}
          onCalendarClose={onCloseDatePicker}
          onInputClick={toggleOpeningDatePicker}
          disabled={isDisabled}
          dateFormat={dateFormat}
          placeholderText={placeholder}
          shouldCloseOnSelect={activeRangeType !== ChartRangeType.Custom}
          showMonthYearPicker={activeRangeType === ChartRangeType.Month}
          showFullMonthYearPicker={activeRangeType === ChartRangeType.Month}
          showYearPicker={activeRangeType === ChartRangeType.Year}
          maxDate={new Date()}
          customInput={<CustomInput />}
        />
      </div>

      {valueForCompare && onChangeValueForCompare && (
        <div className={styles.comparativePeriod}>
          <Checkbox
            label={t("Compare with period")}
            checked={withComparativePeriod}
            className={styles.comparativePeriod__checkbox}
            onChange={() => setWithComparativePeriod?.(!withComparativePeriod)}
          />
          {withComparativePeriod && (
            <DatePicker
              {...(activeRangeType === ChartRangeType.Custom
                ? {
                    selected: startDateForCompare,
                    onChange: handleChangeForCompare,
                    startDate: startDateForCompare,
                    endDate: endDateForCompare,
                    selectsRange: true
                  }
                : activeRangeType === ChartRangeType.Month ||
                    activeRangeType === ChartRangeType.Year
                  ? {
                      selected: startDateForCompare,
                      onChange: (selectedMonthOrYear) =>
                        handleChangeForCompare([selectedMonthOrYear, null])
                    }
                  : {
                      selected: startDateForCompare,
                      selectsRange: true,
                      startDate: startDateForCompare,
                      endDate: endDateForCompare,
                      onChange: handleChangeForCompare
                    })}
              calendarStartDay={1}
              open={isOpenDatePickerForCompare}
              onCalendarClose={onCloseDatePickerForCompare}
              onInputClick={toggleOpeningDatePickerForCompare}
              disabled={isDisabled}
              dateFormat={dateFormat}
              placeholderText={placeholder}
              shouldCloseOnSelect={activeRangeType !== ChartRangeType.Custom}
              showMonthYearPicker={activeRangeType === ChartRangeType.Month}
              showFullMonthYearPicker={activeRangeType === ChartRangeType.Month}
              showYearPicker={activeRangeType === ChartRangeType.Year}
              maxDate={new Date()}
              customInput={<CustomInput label="Period for comparison:" />}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default StatisticChartDatePicker;
